import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Copy, MessageSquare, Check, PenLine, Eye } from "lucide-react";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAutosave } from "../lib/useAutosave";
import { PageTitle, Loading, Emergency911, AiBadge } from "../components/Bits";
import { VoiceInput } from "../components/VoiceInput";
import { BriefCard } from "../components/BriefCard";

const PROMPTS = [
  ["today", "How are they today?", "Slept okay, a little foggy..."],
  ["normal", "What's normal for them?", "Repeats stories, asks for Mom..."],
  ["watch", "What should your helper watch for?", "Restless around 3pm, the back steps..."],
  ["call", "Who should they call?", "Me on my cell. My brother if I don't answer."],
  ["helps", "One thing that always helps", "Old love songs, Jeopardy at 3:30..."],
];
const EMPTY = { today: "", normal: "", watch: "", call: "", helps: "" };

function Editor({ brief, onSave }) {
  const [o, setO] = useState(brief.output);
  const field = (k, label) => (
    <div>
      <label className="block text-base font-semibold">{label}</label>
      <textarea data-testid={`brief-edit-${k}`} className="hf-input mt-1 min-h-[70px]" value={Array.isArray(o[k]) ? o[k].join("\n") : o[k]}
        onChange={(e) => setO({ ...o, [k]: Array.isArray(o[k]) ? e.target.value.split("\n") : e.target.value })} />
    </div>
  );
  return (
    <div className="hf-card space-y-4">
      {field("today", "Today")}
      {field("normal", "What's normal")}
      {field("watch_for", "Watch for (one per line)")}
      {field("who_to_call", "Who to call (one per line)")}
      {field("what_helps", "What always helps")}
      <button data-testid="brief-edit-save" onClick={() => onSave({ ...o, watch_for: o.watch_for.filter(Boolean), who_to_call: o.who_to_call.filter(Boolean) })} className="hf-btn hf-btn-primary w-full">Save changes</button>
    </div>
  );
}

export function BriefNew() {
  const { fid, base, data, roles, helper } = useFamily();
  const nav = useNavigate();
  const [inputs, setInputs, clear] = useAutosave(`handoff_brief_${fid}`, EMPTY);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      const next = data.visits.find((v) => v.status === "upcoming");
      const r = await api.post(`/families/${fid}/briefs`, { inputs, visit_id: next?.id });
      clear();
      nav(`${base}/briefs/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  if (!roles.includes("caregiver")) {
    const b = data.briefs.find((x) => x.status === "approved");
    return <div className="hf-card text-lg">{b ? <Link className="text-hf-green underline" to={`${base}/briefs/${b.id}`}>Open the latest brief</Link> : "No brief has been shared yet."}</div>;
  }
  return (
    <div className="space-y-6">
      <PageTitle eyebrow="Handoff Brief" title="Before the visit, five quick prompts." sub={`Talk or type. Any language is fine. Nothing goes to ${helper?.name || "your helper"} until you approve it.`} />
      {PROMPTS.map(([k, q, ph], i) => (
        <div key={k} className="hf-card hf-rise" style={{ animationDelay: `${i * 60}ms` }}>
          <label className="mb-3 block font-heading text-xl font-semibold">{q}</label>
          <VoiceInput testid={`brief-input-${k}`} value={inputs[k]} placeholder={ph} maxSeconds={45} onChange={(v) => setInputs((cur) => ({ ...cur, [k]: v }))} />
        </div>
      ))}
      <button data-testid="brief-make-button" disabled={busy || !Object.values(inputs).some((v) => v.trim())} onClick={make} className="hf-btn hf-btn-primary w-full">
        <Sparkles className="h-5 w-5" /> {busy ? "Writing your brief..." : "Make my brief"}
      </button>
      <p className="text-center text-base text-hf-muted">Saved as you go, so nothing is lost.</p>
      {data.briefs.length > 0 && (
        <div className="hf-card">
          <p className="hf-eyebrow">Past briefs</p>
          {data.briefs.map((b) => (
            <Link key={b.id} to={`${base}/briefs/${b.id}`} data-testid={`brief-history-${b.id}`} className="flex min-h-[52px] items-center justify-between border-b border-hf-line text-lg last:border-0">
              {fmtDate(b.created_at)} <span className="text-base text-hf-muted">{b.status === "approved" ? "Shared" : "Draft"}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BriefView() {
  const { bid } = useParams();
  const { fid, roles, data, helper, refresh } = useFamily();
  const [b, setB] = useState(null);
  const [edit, setEdit] = useState(false);
  const [orig, setOrig] = useState(false);
  useEffect(() => {
    api.get(`/families/${fid}/briefs/${bid}`).then((r) => setB(r.data)).catch(() => setB(false));
  }, [fid, bid]);
  if (b === null) return <Loading />;
  if (b === false) return <p className="text-lg">This brief isn't available.</p>;
  const isCg = roles.includes("caregiver");
  const link = b.share_token ? `${window.location.origin}/b/${b.share_token}` : "";
  const save = async (output) => {
    const r = await api.put(`/families/${fid}/briefs/${bid}`, { output });
    setB(r.data);
    setEdit(false);
  };
  const approve = async () => {
    const r = await api.post(`/families/${fid}/briefs/${bid}/approve`);
    setB(r.data);
    refresh();
    toast.success("Approved. Ready to share.");
  };
  const msg = `Here's today's Handoff Brief for ${data.family.recipient?.name || "the visit"}. Thank you! ${link}`;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const phone = (helper?.phone || "").replace(/[^\d+]/g, "");
  return (
    <div className="space-y-5">
      <PageTitle eyebrow={b.status === "approved" ? "Shared brief" : "Review your brief"} title={b.status === "approved" ? `Brief for ${fmtDate(b.created_at)}` : "Does this look right?"} />
      <div className="flex flex-wrap items-center gap-2">
        <AiBadge />
        {isCg && (
          <button data-testid="brief-toggle-original" onClick={() => setOrig(!orig)} className="inline-flex min-h-[40px] items-center gap-1 rounded-full bg-white px-4 text-sm font-semibold text-hf-green ring-1 ring-hf-line">
            <Eye className="h-4 w-4" /> {orig ? "Hide your words" : "Show your words"}
          </button>
        )}
      </div>
      {edit ? <Editor brief={b} onSave={save} /> : <BriefCard brief={b} recipient={data.family.recipient?.name} showOriginal={isCg && orig} />}
      {isCg && b.status !== "approved" && !edit && (
        <div className="grid gap-3">
          <button data-testid="brief-approve-button" onClick={approve} className="hf-btn hf-btn-primary"><Check className="h-5 w-5" /> Approve and share</button>
          <button data-testid="brief-edit-button" onClick={() => setEdit(true)} className="hf-btn hf-btn-ghost"><PenLine className="h-5 w-5" /> Edit</button>
          <p className="text-center text-base text-hf-muted">Nothing is shared until you approve.</p>
        </div>
      )}
      {isCg && b.status === "approved" && (
        <div className="hf-card space-y-3" data-testid="brief-share-panel">
          <p className="text-lg">Send this link to {helper?.name || "your helper"}. It opens with no app and no login.</p>
          <p data-testid="brief-share-link" className="break-all rounded-xl bg-hf-cream p-3 text-base text-hf-green">{link}</p>
          <a data-testid="brief-open-messages" href={`sms:${phone}${ios ? "&" : "?"}body=${encodeURIComponent(msg)}`} className="hf-btn hf-btn-coral w-full"><MessageSquare className="h-5 w-5" /> Open in Messages</a>
          <button data-testid="brief-copy-link" onClick={() => { navigator.clipboard?.writeText(msg); toast.success("Copied"); }} className="hf-btn hf-btn-ghost w-full"><Copy className="h-5 w-5" /> Copy message</button>
          <a data-testid="brief-open-public" href={link} target="_blank" rel="noreferrer" className="block text-center text-base font-semibold text-hf-green underline">See it as your helper will</a>
        </div>
      )}
      <Emergency911 />
    </div>
  );
}
